import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Auth } from '@freelancer/auth';
import { Datastore, DatastoreCollection } from '@freelancer/datastore';
import { Task, TasksCollection } from '@freelancer/datastore/collections';
import { LocalStorage } from '@freelancer/local-storage';
import { AnchorScroll, ModalService } from '@freelancer/ui';
import { BannerAlertType } from '@freelancer/ui/banner-alert';
import { Margin } from '@freelancer/ui/margin';
import { ModalResult, ModalSize } from '@freelancer/ui/modal';
import { BackgroundColor, LayoutType } from '@freelancer/ui/page-layout';
import { isDefined, toNumber } from '@freelancer/utils';
import {
  TaskSourceTypeApi,
  TaskStatusApi,
} from 'api-typings/tasklist/tasklist';
import * as Rx from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
  TaskError,
  TaskErrorAction,
  TaskErrorRemedy,
} from './task-error/task-error.model';
import {
  TaskModalAction,
  TaskModalComponent,
  TaskModalResult,
} from './task-modal/task-modal.component';

export const TASK_LIST_KEY = 'taskList';
export const TASK_LIST_CURRENT_TASK_CLEAN_KEY = 'taskListCurrentTaskClean';
enum TaskFormAnchor {
  HEADER = 'task_form_header',
}

@Component({
  selector: 'app-tasklist',
  template: `
    <fl-bit class="PageContainer" flTrackingSection="Tasklist">
      <ng-container *ngIf="formError$ | async as formErr">
        <fl-banner-alert
          *ngIf="formErr.show"
          bannerTitle="Something has gone wrong!"
          i18n-bannerTitle="Task error title"
          [type]="BannerAlertType.ERROR"
          [flMarginBottom]="Margin.SMALL"
          [closeable]="false"
        >
          <ng-container [ngSwitch]="formErr.action">
            <ng-container
              *ngSwitchCase="TaskErrorAction.TASK_TOGGLE"
              i18n="Tasks error message"
            >
              It looks like something has gone wrong while trying to toggle a
              task.
            </ng-container>
            <ng-container
              *ngIf="formErr.action === TaskErrorAction.TASK_CREATE"
              i18n="Tasks error message"
            >
              It looks like something has gone wrong while trying to create a
              task.
            </ng-container>
            <ng-container
              *ngIf="formErr.action === TaskErrorAction.TASK_DELETE"
              i18n="Tasks error message"
            >
              It looks like something has gone wrong while trying to delete a
              task.
            </ng-container>
            <ng-container
              *ngIf="formErr.action === TaskErrorAction.TASK_EDIT"
              i18n="Tasks error message"
            >
              It looks like something has gone wrong while trying to edit a
              task.
            </ng-container>
            <ng-container
              *ngIf="formErr.action === TaskErrorAction.TASK_LOAD"
              i18n="Tasks error message"
            >
              It looks like something has gone wrong while trying to load your
              tasks.
            </ng-container>
            <ng-container
              *ngIf="formErr.action === TaskErrorAction.TASK_LOAD_SPECIFIC"
              i18n="Tasks error message"
            >
              It looks like something has gone wrong while trying to load a
              specific task.
            </ng-container>
            <ng-container
              *ngIf="formErr.action === TaskErrorAction.TASK_SAVE"
              i18n="Tasks error message"
            >
              It looks like something has gone wrong while trying to save your
              task.
            </ng-container>
            <ng-container
              *ngIf="formErr.action === TaskErrorAction.TASK_LIMIT_REACHED"
              i18n="Tasks limit reached error message"
            >
              You have reached the maximum number of tasks for a project.
            </ng-container>
          </ng-container>
          <ng-container [ngSwitch]="formErr.remedy">
            <ng-container
              *ngSwitchCase="TaskErrorRemedy.REFRESH"
              i18n="Task error suggestion"
            >
              Please try refreshing the page.
            </ng-container>
            <ng-container
              *ngSwitchCase="TaskErrorRemedy.REMOVE_TASKS"
              i18n="Task error remove older tasks suggestion"
            >
              Try removing some older tasks and try again.
            </ng-container>
            <ng-container *ngSwitchDefault i18n="Task error suggestion">
              Please try again.
            </ng-container>
          </ng-container>
          <fl-bit *ngIf="formErr.link">
            <ng-container i18n="Task error support contact">
              If the problem persists,
              <fl-link flTrackingLabel="ErrorSupportLink" [link]="formErr.link">
                contact support.
              </fl-link>
            </ng-container>
            <ng-container
              *ngIf="formErr.code && formErr.code !== 'UNKNOWN_ERROR'"
              i18n="Task error code"
            >
              The code for this error is {{ formErr.code }}.
            </ng-container>
          </fl-bit>
        </fl-banner-alert>
      </ng-container>
      <fl-grid [ngClass]="{ FreelancerViewGrid: freelancerView }">
        <fl-col
          [colDesktopSmall]="
            freelancerView ? colDesktopFreelancer : colDesktopList
          "
        >
          <app-task-list
            [disableCreateBtn]="disableCreateBtn"
            [editingTaskId]="editingTaskId"
            [groupedTaskListCollection]="todoTaskListCollection"
            [listTitle]="TaskStatusApi.IN_PROGRESS"
            [padding]="!freelancerView"
            [showCreateBtn]="true"
            (selected)="itemSelectedHandler($event)"
            (newTask)="createNewTaskHandler()"
            (toggled)="formCloseHandler()"
            (hideError)="hideError()"
            (error)="taskListErrorHandler($event)"
          ></app-task-list>
          <app-task-list
            [editingTaskId]="editingTaskId"
            [groupedTaskListCollection]="completedTaskListCollection"
            [listTitle]="TaskStatusApi.CLOSED_RESOLVED"
            [padding]="!freelancerView"
            (selected)="itemSelectedHandler($event)"
            (newTask)="createNewTaskHandler()"
            (toggled)="formCloseHandler()"
            (hideError)="hideError()"
            (error)="taskListErrorHandler($event)"
          ></app-task-list>
        </fl-col>
        <fl-col
          [ngClass]="{ 'FreelancerViewGrid-form': freelancerView }"
          [colDesktopSmall]="
            freelancerView ? colDesktopFreelancer : colDesktopForm
          "
        >
          <fl-anchor-scroll
            [name]="TaskFormAnchor.HEADER"
            [offset]="90"
          ></fl-anchor-scroll>
          <app-task-form-change
            [currentTask]="currentTask"
            [isDraftProject]="isDraftProject"
            [isNewTask]="isNewTask"
            [projectId]="projectId$ | async"
            [showForm]="showForm"
            [taskListCollection]="currentTaskListCollection"
            [userId]="authUId$ | async"
            [isCleanState]="isCleanState"
            [freelancerView]="freelancerView"
            (taskEdited)="updateDraftTask($event)"
            (cleanStateChange)="setCurrentTaskCleanState($event)"
            (error)="taskListErrorHandler($event)"
            (hideError)="hideError()"
            (closeForm)="formCloseHandler()"
          ></app-task-form-change>
        </fl-col>
      </fl-grid>
    </fl-bit>
  `,
  styleUrls: ['./tasks.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TasksComponent implements OnInit, AfterViewInit, OnDestroy {
  BackgroundColor = BackgroundColor;
  BannerAlertType = BannerAlertType;
  LayoutType = LayoutType;
  Margin = Margin;
  TaskErrorAction = TaskErrorAction;
  TaskErrorRemedy = TaskErrorRemedy;
  TaskStatusApi = TaskStatusApi;
  TaskFormAnchor = TaskFormAnchor;

  @Input() projectId$: Rx.Observable<number>;
  @Input() freelancerView = false;

  authUId$: Rx.Observable<number>;
  colDesktopForm = 4;
  colDesktopFreelancer = 11;
  colDesktopList = 8;
  completedTaskListCollection: DatastoreCollection<TasksCollection>;
  currentTask?: Partial<Task>;
  currentTaskListCollection?: DatastoreCollection<TasksCollection>;
  disableCreateBtn = false;
  editingTaskId?: number;
  formError$ = new Rx.BehaviorSubject<TaskError | undefined>(undefined);
  isDraftProject = false;
  isCleanState = false;
  isNewTask = false;
  projectId: number;
  showForm = false;
  todoTaskListCollection: DatastoreCollection<TasksCollection>;

  private errorSubscription: Rx.Subscription | undefined;

  constructor(
    private auth: Auth,
    private datastore: Datastore,
    private modalService: ModalService,
    private ref: ChangeDetectorRef,
    private storageService: LocalStorage,
    private anchorScroll: AnchorScroll,
  ) {}

  ngOnInit() {
    this.authUId$ = this.auth.getUserId().pipe(map(userId => toNumber(userId)));

    const taskSource$ = this.projectId$.pipe(
      map(projectId => {
        this.projectId = projectId;
        return { id: projectId, type: TaskSourceTypeApi.PROJECT };
      }),
    );

    // get all completed tasks
    this.completedTaskListCollection = this.datastore.collection<
      TasksCollection
    >('tasks', query =>
      query
        .where('source', '==', taskSource$)
        .where('status', '==', TaskStatusApi.CLOSED_RESOLVED),
    );

    // get all to do tasks
    this.todoTaskListCollection = this.datastore.collection<TasksCollection>(
      'tasks',
      query =>
        query
          .where('source', '==', taskSource$)
          .where('status', '==', TaskStatusApi.IN_PROGRESS),
    );

    this.errorSubscription = Rx.combineLatest([
      this.completedTaskListCollection.valueChanges(),
      this.todoTaskListCollection.valueChanges(),
    ]).subscribe(([completeTasks, todoTasks]) => {
      if (completeTasks.length >= 500 || todoTasks.length >= 500) {
        this.formError$.next({
          action: TaskErrorAction.TASK_LIMIT_REACHED,
          remedy: TaskErrorRemedy.REMOVE_TASKS,
          show: true,
        });
      }
    });

    this.currentTaskListCollection = this.todoTaskListCollection;
  }

  ngAfterViewInit() {
    // This function will call showItem() which will use anchorScroll.scrollTo().
    // The fl-anchor-scroll should be registered before it is used by scrollTo().
    // Thus, we have to setup the draft project after the view initialised.
    this.setupDraftProject();
  }

  // Checks if the user has a draft task, if they do, open it.
  setupDraftProject(): void {
    Rx.combineLatest([
      this.storageService.get(TASK_LIST_KEY),
      this.storageService.get(TASK_LIST_CURRENT_TASK_CLEAN_KEY),
    ])
      .pipe(take(1))
      .subscribe(([draftTaskState, taskCleanState]) => {
        if (!isDefined(draftTaskState)) {
          return;
        }

        // The `currentTaskClean` in local storage TASK_LIST_KEY has been deprecated.
        // The value from it is for backwards compatibility.
        const { draftTask, currentTaskClean } = draftTaskState;
        // Skip the setup if the draft task does not exists.
        if (!draftTask) {
          return;
        }
        // Get the draft task clean state from TASK_LIST_CURRENT_TASK_CLEAN_KEY in local storage.
        // Use the one from TASK_LIST_KEY as a fallback.
        let isCurrentTaskClean = false;
        if (isDefined(taskCleanState)) {
          isCurrentTaskClean = taskCleanState;
        } else if (isDefined(currentTaskClean)) {
          isCurrentTaskClean = currentTaskClean;
        }

        const isDraftProjectNew = !draftTask.id;
        this.showItem(draftTask, isDraftProjectNew, true, isCurrentTaskClean);
      });
  }

  updateDraftTask(task: Partial<Task> | Task): void {
    this.storageService.set(TASK_LIST_KEY, {
      draftTask: task,
    });
  }

  clearDraftTask(): void {
    this.storageService.set(TASK_LIST_KEY, {
      draftTask: undefined,
    });
  }

  setCurrentTaskCleanState(state: boolean): void {
    this.storageService.set(TASK_LIST_CURRENT_TASK_CLEAN_KEY, state);
  }

  // Called when an item is selected in the item list
  async itemSelectedHandler(selected: Partial<Task>) {
    // prompt the user if he touched stuff in the form
    // to discard or keep changes
    const isCurrentTaskClean = await this.isCurrentTaskClean();
    if (!isCurrentTaskClean) {
      this.openTaskModal().then(response => {
        if (response === TaskModalResult.CANCEL) {
          this.setCurrentTaskCleanState(true);
          this.clearDraftTask();
          this.showItem(selected);
        } else if (response === TaskModalResult.CONFIRM && this.showForm) {
          this.anchorScroll.scrollTo(TaskFormAnchor.HEADER);
        }
      });
    } else {
      this.clearDraftTask();
      this.showItem(selected);
    }
  }

  // Called when the close task form is clicked
  async formCloseHandler() {
    const isCurrentTaskClean = await this.isCurrentTaskClean();
    if (!isCurrentTaskClean) {
      this.openTaskModal().then(response => {
        if (response === TaskModalResult.CANCEL) {
          this.closeForm();
        }
      });
    } else {
      this.closeForm();
    }
  }

  // Called to create a new task
  async createNewTaskHandler() {
    const isCurrentTaskClean = await this.isCurrentTaskClean();
    if (!isCurrentTaskClean) {
      this.openTaskModal().then(response => {
        if (response === TaskModalResult.CANCEL) {
          this.createItem();
        } else if (response === TaskModalResult.CONFIRM && this.showForm) {
          this.anchorScroll.scrollTo(TaskFormAnchor.HEADER);
        }
      });
    } else {
      this.createItem();
    }
  }

  createItem() {
    this.currentTask = undefined;
    this.isNewTask = true;
    this.showForm = true;
    this.setCurrentTaskCleanState(true);
    this.hideError();
    this.disableCreateBtn = true;
    this.anchorScroll.scrollTo(TaskFormAnchor.HEADER);
    this.ref.markForCheck();
  }

  showItem(
    selected: Partial<Task>,
    isNewTask = false,
    isDraftProject = false,
    isCurrentTaskClean = true,
  ) {
    this.setCurrentTaskCleanState(isCurrentTaskClean);
    // if an item was selected while on create mode,
    // display a modal asking if the user wants to keep editing or not
    this.showForm = true;
    this.currentTask = selected;
    this.isDraftProject = isDraftProject;
    this.isCleanState = isCurrentTaskClean;

    if (selected.status === TaskStatusApi.IN_PROGRESS) {
      this.currentTaskListCollection = this.todoTaskListCollection;
    } else {
      this.currentTaskListCollection = this.completedTaskListCollection;
    }

    this.isNewTask = isNewTask;
    this.editingTaskId = selected.id;
    this.hideError();
    this.disableCreateBtn = isNewTask;
    this.anchorScroll.scrollTo(TaskFormAnchor.HEADER);
    this.ref.markForCheck();
  }

  closeForm() {
    this.showForm = false;
    this.isNewTask = false;
    this.setCurrentTaskCleanState(true);
    this.editingTaskId = undefined;
    this.disableCreateBtn = false;
  }

  taskListErrorHandler(error: TaskError) {
    this.formError$.next(error);
  }

  hideError() {
    this.formError$.next(undefined);
  }

  private async openTaskModal(): Promise<ModalResult> {
    return this.modalService
      .open(TaskModalComponent, {
        inputs: {
          action: TaskModalAction.UNSAVED_CHANGES,
          projectId: this.projectId,
        },
        closeable: false,
        size: ModalSize.SMALL,
      })
      .afterClosed()
      .toPromise();
  }

  /** Checks if the current open task is edited. */
  private isCurrentTaskClean(): Promise<boolean> {
    return this.storageService
      .get(TASK_LIST_CURRENT_TASK_CLEAN_KEY)
      .pipe(
        take(1),
        map(
          currentTaskClean =>
            !isDefined(currentTaskClean) || !!currentTaskClean,
        ),
      )
      .toPromise();
  }

  ngOnDestroy(): void {
    this.errorSubscription?.unsubscribe();
  }
}
